import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { useParams } from "react-router-dom";
import axios from "axios";
import {
  Button,
  Group,
  Center,
  MantineProvider,
  Text,
  Title,
  Modal,
  Paper,
  Flex,
  useMantineTheme,
  ActionIcon,
} from "@mantine/core";
import { useTranslation } from "react-i18next";

import Microsite from "@components/Microsite";
import IntroModal from "@components/Microsite/IntroModal";
import RideChallengeApp from "@components/RideChallengeApp";
import { UserLockModal } from "@components/User";
import { TicketEntryForm } from "@components/Ticket";
import { size } from "@util/breakpoints";
import { useMicrositeData } from "@components/Microsite/useMicrositeData";

import { appLanguages } from "@lib/appLanguages";
import { IconLanguage } from "@tabler/icons-react";

export default function MicrositeContainer({ preview }) {
  const theme = useMantineTheme();
  const params = useParams();
  const { t, i18n } = useTranslation();

  // Use the microsite-specific hook to fetch app data
  const {
    campaignId,
    error,
    entityInfo,
    appConfig,
    modalInfo,
    loading,
    initLoading,
    setModalInfo,
  } = useMicrositeData(params.campaignPath, preview);

  axios.interceptors.response.use(function (response) {
    const res = response.data;
    if (res.response_code === 3) {
      setModalInfo(res.response[0]);
      return response;
    } else {
      return response;
    }
  });

  useEffect(() => {
    if (
      entityInfo?.configuration &&
      entityInfo.configuration.languages?.length > 0
    ) {
      // get user's language from localStorage
      const localStorageLanguage = localStorage.getItem("mixer_app_language");
      if (localStorageLanguage) {
        if (entityInfo.configuration.languages.includes(localStorageLanguage)) {
          i18n.changeLanguage(localStorageLanguage);
        } else {
          // set default language to en
          i18n.changeLanguage("en");
          setLocalStorageLanguage("en");
        }
      } else {
        // grab browser language
        const browserLanguage = navigator.language.split("-")[0];
        if (entityInfo.configuration.languages.includes(browserLanguage)) {
          // make sure the browser language is supported
          if (
            appLanguages.some(
              (language) => language.language === browserLanguage
            )
          ) {
            i18n.changeLanguage(browserLanguage);
            setLocalStorageLanguage(browserLanguage);
          } else {
            // set default language to en
            i18n.changeLanguage("en");
            setLocalStorageLanguage("en");
          }
        } else {
          // set default language to en
          i18n.changeLanguage("en");
          setLocalStorageLanguage("en");
        }
      }
    } else {
      // set default language to en
      i18n.changeLanguage("en");
      setLocalStorageLanguage("en");
    }
  }, [JSON.stringify(entityInfo)]);

  function setLocalStorageLanguage(language) {
    localStorage.setItem("mixer_app_language", language);
  }

  // useEffect(() => {
  //   fetchAppData();
  // }, []);

  // function fetchAppData() {
  //   const req = {
  //     url: campaignPath,
  //     campaign_url: campaignPath,
  //   };

  //   if (preview) req.preview = true;
  //   if (effortPath) req.campaign_effort_url = effortPath;

  //   axios
  //     .post(`/microsite/retrieve-by-url/`, req)
  //     .then(({ data }) => {
  //       const res = data.response[0];
  //       setCampaignId(res.campaign_id);
  //       setEffortId(res.campaign_effort_id);
  //       setEntityInfo(res);
  //       setAppConfig(res.app_configuration);

  //       if (res.app_configuration.google_tag_manager) {
  //         if (res.app_configuration.google_tag_manager.gtm_id) {
  //           initializeGtm(res.app_configuration.google_tag_manager.gtm_id);
  //         }
  //       }

  //       if (
  //         data.response[0] &&
  //         data.response[0].language &&
  //         data.response[0].language !== "en"
  //       ) {
  //         i18n.changeLanguage(data.response[0].language);
  //       }
  //     })
  //     .catch((err) => {
  //       setCampaignId(null);
  //       setEffortId(null);
  //       setError(err);
  //     });
  // }

  if (error)
    return (
      <Center style={{ height: 500 }}>
        <Text>{error}</Text>
      </Center>
    );
  if (!entityInfo || (!campaignId && !entityInfo.campaign_effort_id))
    return <div></div>;

  const colors = appConfig && appConfig.colors ? appConfig.colors : null;
  const showMicrosite =
    entityInfo.active_effort_count > 0 || entityInfo.campaign_effort_id;

  return (
    <Layout>
      <MantineProvider
        theme={{
          fontFamily: theme.fontFamily,
          fontFamilyMonospace: theme.fontFamilyMonospace,
          headings: {
            ...theme.headings,
            fontFamily: theme.fontFamily,
          },
          primaryColor: colors ? colors.base : "orange",
        }}
      >
        {entityInfo?.configuration?.languages?.length > 1 && (
          <Flex gap="xs" justify="center" mb="md">
            {entityInfo.configuration.languages
              .filter((m) => appLanguages.some((l) => l.language === m))
              .map((language) => (
                <ActionIcon
                  key={language}
                  onClick={() => i18n.changeLanguage(language)}
                  radius="xl"
                  variant={language === i18n.language ? "filled" : "light"}
                >
                  {appLanguages.find((l) => l.language === language)?.flag || (
                    <IconLanguage size={20} />
                  )}
                </ActionIcon>
              ))}
          </Flex>
        )}
        <UserLockModal data={modalInfo} />
        <div>
          {showMicrosite ? (
            <div>
              {loading || initLoading ? (
                <div></div>
              ) : (
                <>
                  {entityInfo.is_ride_challenge ||
                  entityInfo.is_vehicle_data_effort ? (
                    <RideChallengeApp
                      appConfigData={appConfig}
                      campaignId={campaignId}
                      effortId={entityInfo.campaign_effort_id}
                      entityInfoData={entityInfo}
                      preview={preview}
                    />
                  ) : (
                    <Paper
                      shadow="xl"
                      p="lg"
                      radius="md"
                      style={{
                        // backgroundColor: `rgba(255, 255, 255, 0.975)`,
                        backgroundColor: `rgba(0, 0, 0, 0.85)`,
                        // backgroundColor: "transparent",
                      }}
                    >
                      <Microsite
                        appConfigData={appConfig}
                        campaignId={campaignId}
                        effortId={entityInfo.campaign_effort_id}
                        entityInfoData={entityInfo}
                        preview={preview}
                      />
                    </Paper>
                  )}
                </>
              )}
            </div>
          ) : (
            <Paper
              shadow="xl"
              p="lg"
              radius="md"
              style={{
                backgroundColor: `rgba(0, 0, 0, 0.85)`,
              }}
            >
              <Title c="white" align="center" mb="sm">
                {t("microsite:effort_not_available")}
              </Title>
              <Text c="white" align="center" size="lg">
                {t("microsite:effort_not_available_subtitle", {
                  entity_name: entityInfo.title,
                })}
              </Text>
            </Paper>
          )}
        </div>
      </MantineProvider>
      <Flex gap="xs" mt="lg" justify="center">
        <IntroModal info={entityInfo} />
        {!preview && (
          <TroubleModal
            buttonText={t("microsite:trouble_ticket_button")}
            effortId={entityInfo.campaign_effort_id}
            campaignId={campaignId}
          />
        )}
      </Flex>
    </Layout>
  );
}

const Layout = styled.div`
  width: 600px;
  margin: 0 auto;
  padding: 60px 0 20px 0;
  word-wrap: break-word;
  overflow-x: hidden;

  label {
    color: whitesmoke;
  }

  @media (max-width: ${size.tablet}) {
    width: 97%;
    padding-top: 10px;
  }
`;

const TroubleModal = ({ buttonText, effortId, campaignId }) => {
  const [isOpen, setOpen] = useState(false);

  function onClose() {
    setOpen(false);
  }

  const reqData = {
    url: window.location.href,
  };

  if (effortId) reqData.campaign_effort_id = effortId;
  if (campaignId) reqData.campaign_id = campaignId;

  return (
    <div>
      <Modal opened={isOpen} onClose={onClose}>
        <TicketEntryForm reqData={reqData} />
      </Modal>
      <Button
        size="xs"
        variant="filled"
        color="dark"
        radius="lg"
        onClick={() => setOpen(true)}
      >
        {buttonText}
      </Button>
    </div>
  );
};
